import type { UploadFile } from "@gw/web-basic-components";
import React from "react";
import type { IAttachmentUploadProps, TipsConfig } from "./dto";
import { isValidElement } from "react";
import { isNullOrUndefined } from "../utils";

const getSuffixNameByAccept = (accetp = "") =>
  (accetp.includes("/") ? accetp.split("/") : accetp.split("."))
    ?.pop()
    ?.toLowerCase();

const isAcceptFile = (file: UploadFile, accepts: string[], strict = false) => {
  const suffixs = accepts.map((i) => `.${getSuffixNameByAccept(i)}`);
  const isType = accepts.includes(file.type);
  const isSuffix = !!suffixs.find((i) => file.name.toLowerCase().endsWith(i));
  return strict ? isType && isSuffix : isType || isSuffix;
};

const previewWithNewWindow = (() => {
  // 因为有些文件很大，比如pdf，在fetch的时候耗时过多，所以进行缓存，避免每一次预览大文件都需要等待很长时间
  const cache: Record<string, string> = {};
  return (url: string) => {
    if (url.startsWith("blob:")) {
      return window.open(url);
    }
    if (cache[url]) {
      return window.open(cache[url]);
    }
    // 这边后端接口的url因为头字段中的content-disposition为attachment，所以在浏览器中直接open会直接下载而不是预览，所以需要转为临时url进行预览
    return fetch(url)
      .then((res) => res.blob())
      .then((blob) => {
        const showUrl = URL.createObjectURL(blob);
        window.open(showUrl);
        cache[url] = showUrl;
      })
      .catch(() => window.open(url));
  };
})();

const splitFileName = (name = "") => {
  const nameArr = name.split(".");
  const suffix = nameArr.pop()?.toLowerCase() ?? "";
  return {
    name: nameArr.join("."),
    suffix,
  };
};

const toLowerCaseSuffix = (name = "") => {
  const { name: fileName, suffix } = splitFileName(name);
  return `${fileName}.${suffix}`;
};

const getSuffixNamesByAccept = (accepts: string[]) =>
  accepts.map(getSuffixNameByAccept).join("/");

const renderTipsWithConfig = (tips: TipsConfig) => {
  try {
    const { label, colon = true } = tips;
    let { content } = tips;
    if (typeof content === "string") {
      content = [content];
    }
    return (
      <div className="wbuc-attachment-upload-tips">
        <p className="wbuc-attachment-upload-tips-title">
          {label}
          {colon && "："}
        </p>
        {content.map((i, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <p key={idx}>{i}</p>
        ))}
      </div>
    );
  } catch (e) {
    console.error("【AttachmentUpload】渲染 tips 出错", e, tips);
    return null;
  }
};

const renderTips = (tips: IAttachmentUploadProps["tips"]) => {
  if (
    isNullOrUndefined(tips) ||
    typeof tips !== "object" ||
    isValidElement(tips)
  ) {
    return tips;
  }
  if (Array.isArray(tips)) {
    return tips.map((i) => renderTips(i));
  }
  return renderTipsWithConfig(tips as TipsConfig);
};

/**
 * 下载保存名称
 * @param url 下载地址
 * @param name 保存的名称
 */
const download = (url: string, name?: string) => {
  const link = document.createElement("a");
  link.href = url;
  if (name) {
    link.download = name;
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export {
  getSuffixNameByAccept,
  isAcceptFile,
  previewWithNewWindow,
  toLowerCaseSuffix,
  getSuffixNamesByAccept,
  splitFileName,
  renderTips,
  download,
};
